import fetch from "isomorphic-unfetch";
import { getConfig } from "../../config/app";
import {
  Attribution,
  getGoogleAnalyticsData
} from "../modules/quoteForms/helpers/quoteForms/attribution";

export type InsuredSarlaft = {
  carOwner: {
    isOwner: boolean;
  };
  relation: {
    isOwnerThePayer: boolean;
    isCarFinanced: boolean;
  };
  data: {
    firstName: string;
    lastName: string;
    secondLastName?: string;
    middleName?: string;
    gender: "male" | "female" | "other";
    birthDate: string;
    nationality: { name: string; id: string };
    birthPlace: { name: string; id: string };
  };
  identification: {
    identificationType: "nationalId" | "passport" | "driverLicense";
    identificationNumber: string;
    placeIssued: { name: string; id: number };
    dateIssued: string;
    civilStatus:
      | "single"
      | "married"
      | "divorced"
      | "widowed"
      | "separated"
      | "civilUnion"
      | "widow";
    educationLevel: string;
    childrenQuantity: number;
  };
  address: {
    address: string;
    neighborhood: string;
    city: { name: string; id: number };
    socioeconomicLevel: number;
    housingType: string;
  };
  contact: {
    phone: string;
    landline: string;
    email: string;
  };
  occupationType: {
    occupationType: string;
    ciiuCode?: { name: string; id: string };
    occupation?: string;
    position?: string;
    company?: {
      phone: string;
      type?: string;
      name?: string;
      address: string;
      city: { name: string; id: number };
    };
  };
  financial: {
    monthlyIncome: number;
    monthlyDischarge: number;
    wealth: number;
    liabilityValue: number;
    assetValue: number;
  };
  sourceOfIncome: {
    principalSourceOfIncome: string;
  };
  publicResources: {
    hasPublicPosition: boolean;
    hasPublicLink: boolean;
    managePublicResources: boolean;
    isPubliclyRecognized: boolean;
  };
  foreignTaxation: {
    financialProductCurrency?: string;
    hasForeignCurrencyOperations: boolean;
    hasFinancialProducts: boolean;
  };
  insurance: {
    hasClaims: boolean;
  };
};

type GetOrderProps = {
  formId: string;
};

type UpdateOrderProps = {
  formId: string;
  isPayerSarlaft: boolean;
  data: Partial<InsuredSarlaft>;
  isCompletedFulfillment: boolean;
};

type UploadDocumentsProps = {
  formId: string;
  fileName: string;
  originalFileName?: string;
  file: File;
  isCompletedFulfillment: boolean;
};

type DeleteDocumentsProps = {
  formId: string;
  code: string;
  index?: number;
  isCompletedFulfillment: boolean;
};

type GenerateSarlaftFileProps = {
  formId: string;
  isPayerSarlaft: boolean;
  data: Partial<InsuredSarlaft>;
  isCompletedFulfillment: boolean;
};

type ConfirmFulfillmentProps = {
  formId: string;
  isCompletedFulfillment: boolean;
};

const getFileExtension = (file: File): string => {
  const type = file.type;
  const extension = type.split("/")[1];
  return extension;
};

const getCode = (isPayerSarlaft: boolean): string =>
  isPayerSarlaft ? "payer-sarlaft" : "insured-sarlaft";

const hasDigitallySigned = (fileName: string): string => {
  return (
    fileName === "Sarlaft-asegurado-firmado" ||
    fileName === "Sarlaft-tomador-firmado" ||
    fileName === "Pagare-firmado"
  ).toString();
};

const getDocumentCode = (fileName: string): string => {
  switch (fileName) {
    case "Sarlaft-asegurado-firmado":
      return "insured-sarlaft";
    case "Sarlaft-tomador-firmado":
      return "payer-sarlaft";
    case "Pagare-firmado":
      return "promissory-note";
    case "Identificacion-frente":
      return "identification-document";
    case "Identificacion-reverso":
      return "identification-document-back";
    case "Tarjeta-de-propiedad-frente":
      return "property-certificate";
    case "Tarjeta-de-propiedad-reverso":
      return "property-certificate-back";
    case "Constancia de asesoría":
    case "Solicitud de seguro de vehículo":
      return "insured-sarlaft-extra";
    default:
      return "other-document";
  }
};

export const getOrder = async ({ formId }: GetOrderProps): Promise<any> => {
  const orderOrchestratorUrl = getConfig("orderOrchestrator.baseUrl");

  const rawResponse = await fetch(`${orderOrchestratorUrl}/car-insurance/co/order/${formId}`, {
    method: "GET"
  });
  const content = await rawResponse.json();
  return content;
};

export const updateOrder = async ({
  formId,
  data,
  isPayerSarlaft,
  isCompletedFulfillment
}: UpdateOrderProps): Promise<unknown> => {
  if (isCompletedFulfillment) {
    return;
  }
  const orderOrchestratorUrl = getConfig("orderOrchestrator.baseUrl");
  const code = getCode(isPayerSarlaft);
  const rawResponse = await fetch(
    `${orderOrchestratorUrl}/car-insurance/co/order/${formId}/files/${code}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(data)
    }
  );
  const content = await rawResponse.json();
  return content;
};

export const generateSarlaftFile = async ({
  formId,
  isPayerSarlaft,
  data,
  isCompletedFulfillment
}: GenerateSarlaftFileProps): Promise<unknown> => {
  if (isCompletedFulfillment) {
    return;
  }
  const orderOrchestratorUrl = getConfig("orderOrchestrator.baseUrl");
  const code = getCode(isPayerSarlaft);
  const rawResponse = await fetch(
    `${orderOrchestratorUrl}/car-insurance/co/order/${formId}/files/${code}/generate`,
    {
      method: "PATCH",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json"
      }
    }
  );
  return rawResponse;
};

export const confirmFulfillment = async ({
  formId,
  isCompletedFulfillment
}: ConfirmFulfillmentProps): Promise<unknown> => {
  if (isCompletedFulfillment) {
    return;
  }
  const orderOrchestratorUrl = getConfig("orderOrchestrator.baseUrl");
  const rawResponse = await fetch(
    `${orderOrchestratorUrl}/car-insurance/co/order/${formId}/complete-fulfillment`,
    {
      method: "PATCH"
    }
  );
  const content = await rawResponse.json();
  return content;
};

export const uploadDocuments = async ({
  formId,
  fileName,
  originalFileName,
  file,
  isCompletedFulfillment
}: UploadDocumentsProps): Promise<unknown> => {
  if (isCompletedFulfillment) {
    return;
  }
  const orderOrchestratorUrl = getConfig("orderOrchestrator.baseUrl");
  const code = getDocumentCode(fileName);
  const digitallySigned = hasDigitallySigned(fileName);
  const extension = getFileExtension(file);

  const formData = new FormData();
  formData.append("formId", formId);
  formData.append("code", code);
  formData.append("file", file);
  formData.append("digitallySigned", digitallySigned);
  formData.append("extraData", JSON.stringify({ fileName: `${originalFileName}.${extension}` }));

  const rawResponse = await fetch(`${orderOrchestratorUrl}/car-insurance/co/order/${formId}/file`, {
    method: "POST",
    body: formData
  });
  const content = await rawResponse.json();
  return content;
};

export const deleteDocuments = async ({
  formId,
  code,
  index = 0,
  isCompletedFulfillment
}: DeleteDocumentsProps): Promise<unknown> => {
  if (isCompletedFulfillment) {
    return;
  }
  const orderOrchestratorUrl = getConfig("orderOrchestrator.baseUrl");
  let partialPath = code;

  if (code === "other-document") {
    partialPath = `${partialPath}/${index}`;
  }

  const rawResponse = await fetch(
    `${orderOrchestratorUrl}/car-insurance/co/order/${formId}/files/${partialPath}`,
    {
      method: "DELETE"
    }
  );
  const content = await rawResponse.json();
  return content;
};

type OrderAttribution = Partial<Omit<Attribution, "ip">> & {
  ip: string | null;
  userType: string | null;
};

const createOrderAttribution = (): OrderAttribution => {
  const appName = getConfig("appName");
  const appVersion = getConfig("appVersion");
  return {
    app: {
      name: appName,
      version: appVersion
    },
    ga: getGoogleAnalyticsData(),
    userType: "unknown",
    ip: null
  };
};

export const createOrderThroughMarketplaceBff = async (
  quoteId: string,
  offerId: string,
  countryCode: string
): Promise<void> => {
  try {
    const marketplaceBffUrl = getConfig("marketplaceBff.baseUrl");
    await fetch(`${marketplaceBffUrl}/car-insurance/${countryCode}/orders`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        offerId,
        quoteId,
        attribution: createOrderAttribution()
      })
    });
  } catch (error) {
    if (error instanceof Response && error.status === 409) {
      throw new Error("The order already exists");
    }
  }
};
